import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listUsers, setActive, setRole, type AdminUser, type Role } from '../../api/admin';
import { Alert, Badge, Button, DataTable, EmptyState, Input, LoadingState, Select } from '../ui';
import type { BadgeTone } from '../ui';

const ROLES: Role[] = ['MEMBER', 'MODERATOR', 'ADMIN'];

const TONES: Record<Role, BadgeTone> = {
  MEMBER: 'neutral',
  MODERATOR: 'accent',
  ADMIN: 'brand',
};

/**
 * People, and what they may do.
 *
 * Deactivating asks for a reason before it will go through — the backend
 * refuses without one, and asking here rather than reporting a refusal is the
 * same rule said twice, in the right order.
 */
export function AdminPeople() {
  const [people, setPeople] = useState<AdminUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async (search: string) => {
    try {
      const page = await listUsers(search ? { q: search } : {});
      setPeople(page.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setPeople([]);
    }
  }, []);

  useEffect(() => {
    void load(query);
  }, [load, query]);

  const run = async (handle: string, action: () => Promise<unknown>) => {
    setWorking(handle);
    setError(null);
    try {
      await action();
      await load(query);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  const deactivate = (person: AdminUser) => {
    const reason = window.prompt(`Why is @${person.handle} being deactivated?`);
    if (reason === null || !reason.trim()) {
      return;
    }
    void run(person.handle, () => setActive(person.handle, false, reason.trim()));
  };

  return (
    <>
      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      <Input
        label="Find somebody"
        placeholder="Handle, name or email"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {people === null ? (
        <LoadingState label="Reading the accounts…" />
      ) : (
        <DataTable
          caption="People on this instance"
          rows={people}
          rowKey={(person) => person.id}
          empty={<EmptyState title="Nobody matches that" description="Try a shorter search." />}
          columns={[
            {
              key: 'person',
              header: 'Person',
              render: (person) => (
                <>
                  <strong>{person.display_name}</strong>
                  <span className="sc-admin__muted"> @{person.handle}</span>
                  {!person.is_active && (
                    <>
                      {' '}
                      <Badge tone="warning">Deactivated</Badge>
                    </>
                  )}
                </>
              ),
            },
            {
              key: 'role',
              header: 'Role',
              render: (person) => <Badge tone={TONES[person.role]}>{person.role}</Badge>,
            },
            { key: 'stories', header: 'Stories', numeric: true, render: (p) => p.story_count },
            {
              key: 'change',
              header: 'Change role',
              render: (person) => (
                // A real `<label for>` rather than an `aria-label`: the
                // column heading names the control once for everybody, and
                // this names *which row* for somebody who cannot see it.
                <Select
                  label={`Role for @${person.handle}`}
                  labelHidden
                  className="sc-admin__select"
                  value={person.role}
                  disabled={person.is_me || working === person.handle}
                  options={ROLES.map((role) => ({ value: role, label: role }))}
                  onChange={(event) =>
                    void run(person.handle, () =>
                      setRole(person.handle, event.target.value as Role),
                    )
                  }
                />
              ),
            },
            {
              key: 'active',
              header: 'Account',
              render: (person) =>
                person.is_me ? (
                  <span className="sc-admin__muted">This is you</span>
                ) : person.is_active ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={working === person.handle}
                    onClick={() => deactivate(person)}
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={working === person.handle}
                    onClick={() => void run(person.handle, () => setActive(person.handle, true))}
                  >
                    Reactivate
                  </Button>
                ),
            },
          ]}
        />
      )}
    </>
  );
}
